'use client';

import { c as _c } from "react/compiler-runtime";
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { StickyFooter } from '~/components/StickyFooter';
import { getPathname } from '~/i18n/navigation';
import { trackFunnelEvent } from '~/utils/funnelTracking';
import { pricePerPerson as pricePerPersonForPax } from '~/utils/paxHelpers';
import { scrollIntoViewSettled } from '~/utils/scrollIntoViewSettled';
import { useTripOverviewSummary } from '../hooks/useTripOverviewSummary';
import { useTripPriceSummary } from '../hooks/useTripPriceSummary';
import type { TripOverviewIncludedItem } from '../types';
import { TripLoyaltyPriceRow } from './TripLoyaltyPriceRow';
import { TripPriceBreakdownModal } from './TripPriceBreakdownModal';
type TripPriceBarProps = {
  whatsIncluded: TripOverviewIncludedItem[];
  ctaLabel?: string;
  ctaHref?: string;
  ctaEventName?: string;
};
export const TripPriceBar = t0 => {
  const $ = _c(42);
  const {
    whatsIncluded,
    ctaLabel,
    ctaHref,
    ctaEventName: t1
  } = t0;
  const ctaEventName = t1 === undefined ? "trip_funnel_next_accommodation" : t1;
  const t = useTranslations("journey");
  const tForms = useTranslations("forms");
  const locale = useLocale();
  const router = useRouter();
  const {
    tripId,
    originCity,
    dateRange,
    travellerCount,
    adultCount,
    childAges,
    rooms,
    nights,
    isReadOnly,
    datesConfirmed,
    isLiveChecking
  } = useTripOverviewSummary();
  const {
    totalPrice,
    rate,
    displayCurrency,
    isPriceLoading
  } = useTripPriceSummary();
  let t2;
  if ($[0] !== adultCount || $[1] !== childAges || $[2] !== totalPrice) {
    t2 = totalPrice !== undefined ? pricePerPersonForPax(totalPrice, adultCount, childAges) : undefined;
    $[0] = adultCount;
    $[1] = childAges;
    $[2] = totalPrice;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const pricePerPersonGBP = t2;
  let t3;
  if ($[4] !== pricePerPersonGBP || $[5] !== rate) {
    t3 = pricePerPersonGBP !== undefined ? Math.round(pricePerPersonGBP * rate) : undefined;
    $[4] = pricePerPersonGBP;
    $[5] = rate;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const price = t3;
  const [isModalOpen, setIsModalOpen] = useState(false);
  if (price === undefined && !isPriceLoading) {
    return null;
  }
  const showConfirmDatesCta = ctaLabel === undefined && ctaHref === undefined && !isReadOnly && !datesConfirmed;
  let t4;
  if ($[7] !== ctaLabel || $[8] !== showConfirmDatesCta || $[9] !== t || $[10] !== tForms) {
    t4 = showConfirmDatesCta ? tForms("confirm_dates") : ctaLabel ?? t("overview_booking_card_next_accommodation");
    $[7] = ctaLabel;
    $[8] = showConfirmDatesCta;
    $[9] = t;
    $[10] = tForms;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const resolvedCtaLabel = t4;
  const resolvedCtaHref = ctaHref ?? `/journey/accommodation/${tripId}`;
  let t5;
  if ($[12] !== ctaEventName || $[13] !== locale || $[14] !== resolvedCtaHref || $[15] !== router || $[16] !== showConfirmDatesCta) {
    t5 = () => {
      if (showConfirmDatesCta) {
        trackFunnelEvent("trip_funnel_confirm_dates_prompt", {
          location: "mobile_bar"
        });
        const datesAnchor = globalThis.document.getElementById("overview-booking-dates");
        if (datesAnchor) {
          scrollIntoViewSettled(datesAnchor);
        }
        return;
      }
      trackFunnelEvent(ctaEventName, {
        location: "mobile_bar"
      });
      router.push(getPathname({
        href: resolvedCtaHref,
        locale
      }));
    };
    $[12] = ctaEventName;
    $[13] = locale;
    $[14] = resolvedCtaHref;
    $[15] = router;
    $[16] = showConfirmDatesCta;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleCtaClick = t5;
  let t6;
  if ($[18] !== t) {
    t6 = t("sticky_footer_price_suffix");
    $[18] = t;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsModalOpen(true);
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== dateRange || $[22] !== displayCurrency || $[23] !== handleCtaClick || $[24] !== isLiveChecking || $[25] !== price || $[26] !== resolvedCtaLabel || $[27] !== t6 || $[28] !== travellerCount) {
    t8 = <StickyFooter dateRange={dateRange} travellerCount={travellerCount} price={price} currency={displayCurrency} priceLabel={t6} PriceRow={TripLoyaltyPriceRow} ctaLabel={resolvedCtaLabel} onCtaClick={handleCtaClick} isCtaDisabled={isLiveChecking} onPriceClick={t7} />;
    $[21] = dateRange;
    $[22] = displayCurrency;
    $[23] = handleCtaClick;
    $[24] = isLiveChecking;
    $[25] = price;
    $[26] = resolvedCtaLabel;
    $[27] = t6;
    $[28] = travellerCount;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setIsModalOpen(false);
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== dateRange || $[32] !== isModalOpen || $[33] !== nights || $[34] !== originCity || $[35] !== rooms || $[36] !== travellerCount || $[37] !== whatsIncluded) {
    t10 = <TripPriceBreakdownModal isOpen={isModalOpen} close={t9} whatsIncluded={whatsIncluded} originCity={originCity} dateRange={dateRange} travellerCount={travellerCount} rooms={rooms} nights={nights} />;
    $[31] = dateRange;
    $[32] = isModalOpen;
    $[33] = nights;
    $[34] = originCity;
    $[35] = rooms;
    $[36] = travellerCount;
    $[37] = whatsIncluded;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== t10 || $[40] !== t8) {
    t11 = <>{t8}{t10}</>;
    $[39] = t10;
    $[40] = t8;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  return t11;
};